import { useRef, useEffect, useState, FC, ReactNode, CSSProperties, RefObject } from "react";

const G = {
  gold: "#C9A84C", goldL: "#E2C06A", goldS: "#FFD97D", goldD: "#A07830",
  navy: "#0D1F35", navyM: "#0A1628", navyD: "#060F1E",
  cream: "#F7F3EB", creamL: "#FAFAF7", white: "#FFFFFF",
  tMid: "#4A5568", tLight: "#8A96A3", teal: "#4ECDC4",
  border: "#E2E8F0",
};

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');
*{box-sizing:border-box;margin:0;padding:0;}
html{scroll-behavior:smooth;}
body{font-family:'DM Sans',sans-serif;overflow-x:hidden;}
@keyframes pulseTravel{0%{offset-distance:0%;opacity:0;}8%{opacity:1;}92%{opacity:1;}100%{offset-distance:100%;opacity:0;}}
@keyframes reverseFlow{0%{offset-distance:100%;opacity:0;}8%{opacity:1;}92%{opacity:1;}100%{offset-distance:0%;opacity:0;}}
@keyframes bloomPulse{0%,100%{opacity:.4;}50%{opacity:1;}}
@keyframes rotateSlow{from{transform:rotate(0deg);}to{transform:rotate(360deg);}}
@keyframes circuitFlicker{0%,100%{opacity:.15;}50%{opacity:.7;}}
@keyframes fadeUp{from{opacity:0;transform:translateY(28px);}to{opacity:1;transform:translateY(0);}}
@keyframes shimmer{0%{background-position:-200% center;}100%{background-position:200% center;}}
@keyframes waPulse{0%,100%{box-shadow:0 0 0 0 rgba(37,211,102,.5);}60%{box-shadow:0 0 0 14px rgba(37,211,102,0);}}
@keyframes scrollBounce{0%,100%{transform:translateY(0);}50%{transform:translateY(6px);}}

/* Scroll reveal */
.sr{opacity:0;transform:translateY(36px) scale(.98);transition:opacity .9s cubic-bezier(0.22,1,0.36,1),transform .9s cubic-bezier(0.22,1,0.36,1);will-change:transform,opacity;}
.sr.vis{opacity:1;transform:translateY(0) scale(1);}
.sr-l{opacity:0;transform:translateX(-44px);transition:opacity .9s cubic-bezier(0.22,1,0.36,1),transform .9s cubic-bezier(0.22,1,0.36,1);will-change:transform,opacity;}
.sr-l.vis{opacity:1;transform:translateX(0);}
.sr-r{opacity:0;transform:translateX(44px);transition:opacity .9s cubic-bezier(0.22,1,0.36,1),transform .9s cubic-bezier(0.22,1,0.36,1);will-change:transform,opacity;}
.sr-r.vis{opacity:1;transform:translateX(0);}
.stag>.sr:nth-child(1){transition-delay:0ms;}
.stag>.sr:nth-child(2){transition-delay:110ms;}
.stag>.sr:nth-child(3){transition-delay:220ms;}
.stag>.sr:nth-child(4){transition-delay:330ms;}
.stag>.sr:nth-child(5){transition-delay:440ms;}
.stag>.sr:nth-child(6){transition-delay:550ms;}

/* Buttons */
.btn-g{background:linear-gradient(135deg,${G.gold},${G.goldL});color:${G.navy};font-family:'DM Sans',sans-serif;font-weight:600;font-size:13px;letter-spacing:1.5px;text-transform:uppercase;padding:15px 36px;border:none;border-radius:3px;cursor:pointer;box-shadow:0 6px 22px rgba(201,168,76,.3);transition:transform .25s cubic-bezier(0.22,1,0.36,1),box-shadow .25s,filter .25s;will-change:transform;}
.btn-g:hover{transform:translateY(-3px) scale(1.03);box-shadow:0 14px 36px rgba(201,168,76,.45);filter:brightness(1.06);}
.btn-g:active{transform:scale(.97);}
.btn-o{background:transparent;color:${G.gold};font-family:'DM Sans',sans-serif;font-weight:600;font-size:12px;letter-spacing:1.5px;text-transform:uppercase;padding:14px 30px;border:1px solid ${G.gold};border-radius:3px;cursor:pointer;transition:all .25s cubic-bezier(0.22,1,0.36,1);}
.btn-o:hover{background:${G.gold};color:${G.navy};transform:translateY(-2px);}

/* Cards */
.card{transition:transform .4s cubic-bezier(0.22,1,0.36,1),box-shadow .4s cubic-bezier(0.22,1,0.36,1);}
.card:hover{transform:translateY(-7px);box-shadow:0 20px 56px rgba(13,31,53,.13)!important;}

/* Nav link */
.nl{position:relative;cursor:pointer;transition:color .3s;font-size:11px;font-weight:500;letter-spacing:2px;text-transform:uppercase;}
.nl::after{content:'';position:absolute;bottom:-4px;left:0;width:0;height:1px;background:${G.gold};transition:width .4s cubic-bezier(0.22,1,0.36,1);}
.nl:hover::after,.nl.act::after{width:100%;}
.nl.act{color:${G.gold}!important;}

/* Input */
input:focus{outline:none;border-color:${G.gold}!important;box-shadow:0 0 0 3px rgba(201,168,76,.18),0 2px 10px rgba(201,168,76,.1);}
::-webkit-scrollbar{width:4px;}::-webkit-scrollbar-thumb{background:${G.gold};border-radius:4px;}
.wa{animation:waPulse 2.5s infinite;}
.wa:hover{transform:scale(1.12)!important;}

@media (max-width:768px){
  .desktop-menu{display:none!important;}
  .mobile-menu-btn{display:block!important;}
}
`;

function inject() {
  if (document.getElementById("hva2")) return;
  const s = document.createElement("style"); s.id = "hva2"; s.textContent = CSS;
  document.head.appendChild(s);
}

// ─── HOOKS ────────────────────────────────────────────────────────────────────
function useScrollReveal() {
  useEffect(() => {
    const update = () => {
      document.querySelectorAll(".sr,.sr-l,.sr-r").forEach(el => {
        const r = el.getBoundingClientRect();
        if (r.top < window.innerHeight * .88) el.classList.add("vis");
      });
    };
    window.addEventListener("scroll", update, { passive: true });
    update();
    return () => window.removeEventListener("scroll", update);
  });
}

function useScrollProgress(ref: RefObject<HTMLDivElement | null>) {
  const [p, setP] = useState(0);
  useEffect(() => {
    const fn = () => {
      const el = ref.current; if (!el) return;
      const r = el.getBoundingClientRect();
      const total = r.height - window.innerHeight;
      setP(Math.max(0, Math.min(1, -r.top / total)));
    };
    window.addEventListener("scroll", fn, { passive: true }); fn();
    return () => window.removeEventListener("scroll", fn);
  }, [ref]);
  return p;
}

function useActiveSection(ids: string[]) {
  const [act, setAct] = useState(ids[0]);
  useEffect(() => {
    const io = new IntersectionObserver(
      es => es.forEach(e => { if (e.isIntersecting) setAct(e.target.id); }), { threshold: .35 }
    );
    ids.forEach(id => { const el = document.getElementById(id); if (el) io.observe(el); });
    return () => io.disconnect();
  }, []);
  return act;
}

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const c01 = (v: number, lo: number, hi: number) => Math.max(0, Math.min(1, (v - lo) / (hi - lo)));
const eoe = (t: number) => t === 1 ? 1 : 1 - Math.pow(2, -10 * t);

// ─── PATHS ───────────────────────────────────────────────────────────────────
const PA = "M 168,100 C 230,100 240,100 302,100";
const PB1 = "M 436,100 C 498,100 508,100 570,100";
const PB2 = "M 436,130 C 460,165 468,195 570,215";
const PC = "M 704,100 C 766,100 776,100 838,100";
// Reverse: battery → home
const PREV = "M 704,215 C 760,215 780,162 838,132";

// ─── PULSE ───────────────────────────────────────────────────────────────────
const Pulse: FC<{ path: string; delay: number; dur: number; color?: string; rev?: boolean }> =
  ({ path, delay, dur, color = G.goldS, rev = false }) => (
    <rect width="14" height="3" rx="2" fill={color} style={{
      filter: window.innerWidth < 768 ? "none" : "blur(2px)",
      willChange: "transform,opacity",
      offsetPath: `path('${path}')`,
      offsetDistance: rev ? "100%" : "0%",
      animation: `${rev ? "reverseFlow" : "pulseTravel"} ${dur}s ${delay}s cubic-bezier(0.22,1,0.36,1) infinite`,
    } as any} />
  );

// ─── ENERGY BG CANVAS ────────────────────────────────────────────────────────
const EnergyBg: FC<{ progress: number }> = ({ progress }) => {
  const b1 = eoe(c01(progress, 0, .25));
  const b2 = eoe(c01(progress, .25, .5));
  const b3 = eoe(c01(progress, .5, .75));
  const b4 = eoe(c01(progress, .75, 1));
  const xray = eoe(c01(progress, .3, .65));
  const night = b4;

  const bgR = Math.round(lerp(247, 6, night));
  const bgG = Math.round(lerp(243, 15, night));
  const bgB = Math.round(lerp(235, 30, night));

  const stars = [[80, 30], [160, 15], [290, 55], [420, 25], [560, 45], [680, 20], [800, 50], [920, 32], [960, 42], [200, 28], [500, 18], [740, 38], [350, 60], [650, 12]];

  return (
    <div style={{
      position: "absolute", inset: 0, overflow: "hidden", zIndex: 0,
      background: `rgb(${bgR},${bgG},${bgB})`, transition: "background 1.2s cubic-bezier(0.22,1,0.36,1)"
    }}>

      {/* Stars */}
      {stars.map(([x, y], i) => (
        <div key={i} style={{
          position: "absolute", left: x, top: y, width: 2, height: 2,
          borderRadius: "50%", background: G.white, opacity: night * .7,
          transition: "opacity 1s ease", pointerEvents: "none"
        }} />
      ))}

      {/* Radial bloom */}
      <div style={{
        position: "absolute", top: "50%", left: "50%",
        transform: "translate(-50%,-50%)", width: "70%", paddingBottom: "70%", borderRadius: "50%",
        background: `radial-gradient(circle,rgba(201,168,76,${lerp(.03, .1, b1)}) 0%,transparent 70%)`,
        transition: "background .6s ease", pointerEvents: "none"
      }} />

      {/* SVG diagram — full bleed, low opacity */}
      <svg viewBox="0 0 1000 280" preserveAspectRatio="xMidYMid meet"
        style={{
          position: "absolute", top: "50%", left: "50%",
          transform: "translate(-50%,-50%)", width: "90%", maxWidth: 960, opacity: .18, pointerEvents: "none"
        }}>

        {/* Paths */}
        <path d={PA} fill="none" stroke={G.gold} strokeWidth="1.5" opacity={lerp(.1, .7, b1)} strokeDasharray="6 4" />
        <path d={PB1} fill="none" stroke={G.gold} strokeWidth="1.5" opacity={lerp(.1, .7, b2)} strokeDasharray="6 4" />
        <path d={PB2} fill="none" stroke={G.gold} strokeWidth="1" opacity={lerp(.1, .5, b3)} strokeDasharray="4 6" />
        <path d={PC} fill="none" stroke={G.gold} strokeWidth="1.5" opacity={lerp(.1, .7, b2)} strokeDasharray="6 4" />
        <path d={PREV} fill="none" stroke={G.teal} strokeWidth="1.5" opacity={lerp(0, .7, b4)} strokeDasharray="5 4" />

        {/* Pulses */}
        {b1 > .1 && [0, .45, .9, 1.35].map((d, i) => <Pulse key={i} path={PA} delay={d} dur={2.2} />)}
        {b2 > .1 && [0, .5, 1].map((d, i) => <Pulse key={i} path={PB1} delay={d} dur={2.4} />)}
        {b2 > .2 && [.25, .85, 1.45].map((d, i) => <Pulse key={i} path={PB2} delay={d} dur={2.8} color={G.gold} />)}
        {b4 > .1 && [0, .5, 1, 1.5].map((d, i) => <Pulse key={i} path={PREV} delay={d} dur={2} color={G.teal} rev />)}

        {/* Nodes — minimal circles */}
        {([
          [112, 110, b1], [370, 110, Math.max(b1 * .4, b2)],
          [634, 160, Math.max(b2 * .3, b3)], [890, 110, Math.max(b2 * .4, b4)]
        ] as [number, number, number][]).map(([cx, cy, g], i) => (
          <g key={i}>
            <circle cx={cx} cy={cy} r="52" fill={G.navy} stroke={G.gold}
              strokeWidth={lerp(.5, 2, g)} opacity={lerp(.4, .95, g)} />
            <circle cx={cx} cy={cy} r="62" fill="none" stroke={G.gold}
              strokeWidth=".5" opacity={lerp(0, .4, g)} />
          </g>
        ))}
      </svg>
    </div>
  );
};

// ─── COMPONENTS ───────────────────────────────────────────────────────────────
const Logo: FC<{ size?: number; light?: boolean }> = ({ size = 30, light = false }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <circle cx="24" cy="24" r="9" fill={G.gold} />
      <circle cx="24" cy="24" r="14" fill="none" stroke={G.gold} strokeWidth="1" strokeDasharray="2 3.5" opacity=".7" />
      {[0, 45, 90, 135, 180, 225, 270, 315].map((deg, i) => (
        <line key={i} x1="24" y1="2" x2="24" y2="8" stroke={G.goldL} strokeWidth="2" strokeLinecap="round" transform={`rotate(${deg} 24 24)`} />
      ))}
    </svg>
    <span style={{
      fontFamily: "'Cormorant Garamond',serif", fontSize: size * .75, fontWeight: 700,
      letterSpacing: 4, textTransform: "uppercase" as const, color: light ? G.white : G.navy
    }}>
      Helio<span style={{ color: G.gold }}>VAA</span>
    </span>
  </div>
);

const GLine: FC<{ center?: boolean }> = ({ center }) => (
  <div style={{
    width: 52, height: 2, background: `linear-gradient(90deg,${G.gold},${G.goldL})`,
    borderRadius: 2, margin: center ? "0 auto 28px" : "0 0 28px"
  }} />
);

const Eyebrow: FC<{ children: ReactNode; center?: boolean; light?: boolean }> = ({ children, center, light }) => (
  <p style={{
    fontSize: 10, fontWeight: 600, letterSpacing: 3, textTransform: "uppercase" as const,
    color: light ? G.goldL : G.gold, marginBottom: 14, textAlign: center ? "center" : "left", fontFamily: "DM Sans"
  }}>
    {children}
  </p>
);

const WaIcon = () => (
  <svg width="18" height="18" viewBox="0 0 32 32" fill="white">
    <path d="M16 2C8.268 2 2 8.268 2 16c0 2.47.672 4.782 1.84 6.768L2 30l7.42-1.814A13.93 13.93 0 0016 30c7.732 0 14-6.268 14-14S23.732 2 16 2zm7.498 19.666c-.31.872-1.82 1.666-2.478 1.72-.657.054-1.284.31-4.33-.902-3.658-1.452-5.983-5.2-6.162-5.44-.178-.24-1.456-1.94-1.456-3.7 0-1.76.92-2.626 1.248-2.984.328-.358.716-.448.954-.448l.686.013c.22.01.516-.083.808.617.31.73 1.05 2.526 1.14 2.71.09.185.15.4.03.643-.12.245-.18.397-.358.61-.178.214-.374.478-.534.642-.178.18-.363.375-.156.736.208.36.923 1.52 1.98 2.462 1.36 1.21 2.507 1.585 2.867 1.764.36.178.57.15.78-.09.21-.24.9-1.05 1.14-1.41.238-.36.477-.3.803-.18.326.12 2.07.977 2.43 1.155.36.178.6.267.688.414.09.147.09.85-.22 1.722z" />
  </svg>
);

const WaBtn: FC<{ floating?: boolean }> = ({ floating }) => {
  const href = "https://wa.me/917306061397";
  if (floating) return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="wa"
      style={{
        position: "fixed", bottom: 28, right: 28, zIndex: 999, background: "#25D366",
        borderRadius: "50%", width: 54, height: 54, display: "flex", alignItems: "center",
        justifyContent: "center", textDecoration: "none", transition: "transform .2s"
      }}>
      <WaIcon />
    </a>
  );
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
      style={{
        display: "inline-flex", alignItems: "center", gap: 8, background: "#25D366", color: G.white,
        fontFamily: "'DM Sans',sans-serif", fontWeight: 600, fontSize: 13, padding: "14px 24px",
        borderRadius: 3, textDecoration: "none", letterSpacing: ".5px",
        transition: "all .25s", boxShadow: "0 4px 16px rgba(37,211,102,.35)"
      }}>
      <WaIcon />WhatsApp Us
    </a>
  );
};

// ─── NAV ─────────────────────────────────────────────────────────────────────
const Nav: FC<{ scrollTo: (id: string) => void }> = ({ scrollTo }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const active = useActiveSection(["hero", "howitworks", "solution", "process", "calculator", "testimonials", "contact"]);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 100, height: 64,
      background: scrolled ? "rgba(13,31,53,.97)" : "rgba(13,31,53,.75)",
      backdropFilter: "blur(16px)",
      borderBottom: `1px solid rgba(201,168,76,${scrolled ? .2 : 0})`,
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "0 36px", transition: "all .4s cubic-bezier(0.22,1,0.36,1)"
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
        <Logo size={26} light />

        {/* Desktop Menu */}
        <div className="desktop-menu" style={{ display: "flex", gap: 28, alignItems: "center" }}>
          {[["howitworks", "How It Works"], ["solution", "Solutions"], ["process", "Process"], ["calculator", "Calculator"], ["testimonials", "Reviews"]].map(([id, label]) => (
            <span key={id} className={`nl${active === id ? " act" : ""}`}
              onClick={() => scrollTo(id)}
              style={{ color: active === id ? G.gold : "rgba(255,255,255,.6)" }}>
              {label}
            </span>
          ))}
          <button className="btn-o" onClick={() => scrollTo("contact")} style={{ padding: "8px 18px", fontSize: 10 }}>
            Get Estimate
          </button>
        </div>

        {/* Mobile Toggle */}
        <div
          className="mobile-menu-btn"
          onClick={() => setOpen(!open)}
          style={{ display: "none", cursor: "pointer", padding: 6 }}
        >
          {[0, 1, 2].map(i => (
            <div key={i} style={{
              width: 22,
              height: 2,
              background: G.gold,
              margin: "5px 0",
              borderRadius: 2,
              boxShadow: "0 0 6px rgba(201,168,76,0.6)",
              transition: "all .3s cubic-bezier(0.22,1,0.36,1)"
            }} />
          ))}
        </div>
      </div>

      {open && (
        <div style={{
          position: "absolute",
          top: 64,
          left: 0,
          right: 0,
          background: "rgba(13,31,53,.98)",
          backdropFilter: "blur(20px)",
          padding: "24px",
          display: "flex",
          flexDirection: "column",
          gap: 18,
          borderTop: "1px solid rgba(201,168,76,0.2)"
        }}>
          {[["howitworks", "How It Works"], ["solution", "Solutions"], ["process", "Process"], ["calculator", "Calculator"], ["testimonials", "Reviews"]].map(([id, label]) => (
            <span key={id}
              onClick={() => { scrollTo(id); setOpen(false); }}
              style={{
                color: G.white,
                fontSize: 14,
                letterSpacing: 2,
                textTransform: "uppercase",
                cursor: "pointer"
              }}>
              {label}
            </span>
          ))}

          <button className="btn-g" onClick={() => { scrollTo("contact"); setOpen(false); }}>
            Get Estimate
          </button>
        </div>
      )}
    </nav>
  );
};

// ─── HERO ─────────────────────────────────────────────────────────────────────
const Hero: FC<{ scrollTo: (id: string) => void }> = ({ scrollTo }) => {
  return (
    <section id="hero" style={{
      minHeight: "100vh",
      background: `linear-gradient(160deg,${G.cream} 0%,${G.creamL} 100%)`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "100px 24px 80px",
      position: "relative",
      overflow: "hidden"
    }}>

      <div style={{
        maxWidth: 1100,
        width: "100%",
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))",
        gap: 48,
        alignItems: "center"
      }}>

        {/* LEFT CONTENT */}
        <div>
          <div style={{
            display: "inline-block",
            padding: "6px 14px",
            borderRadius: 20,
            background: "rgba(78,205,196,0.15)",
            color: G.teal,
            fontSize: 11,
            fontWeight: 600,
            marginBottom: 16
          }}>
            Kerala’s Trusted Solar Partner · KSEB Empanelled
          </div>

          <h1 style={{
            fontFamily: "'Cormorant Garamond',serif",
            fontSize: "clamp(2.4rem,5vw,3.4rem)",
            fontWeight: 700,
            color: G.navy,
            lineHeight: 1.1,
            marginBottom: 20
          }}>
            Power Your Home,<br />
            <span style={{ color: G.gold }}>Zero the Bill</span>
          </h1>

          <p style={{
            color: G.tMid,
            lineHeight: 1.8,
            fontSize: 15,
            maxWidth: 480,
            marginBottom: 28
          }}>
            Kerala's rising electricity tariffs make solar your smartest financial move.
            We handle everything — from KSEB application to net metering — in under 45 days.
          </p>

          <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            <button className="btn-g" onClick={() => scrollTo("calculator")}>
              ⚡ Calculate My Savings
            </button>
            <WaBtn />
          </div>

          <div style={{
            display: "flex",
            gap: 28,
            marginTop: 36,
            flexWrap: "wrap"
          }}>
            {["₹78K Subsidy", "45 Days Setup", "500+ Homes"].map((t, i) => (
              <div key={i}>
                <div style={{
                  fontFamily: "'Cormorant Garamond',serif",
                  fontSize: 22,
                  fontWeight: 700,
                  color: G.navy
                }}>{t.split(" ")[0]}</div>
                <div style={{ fontSize: 12, color: G.tLight }}>{t.split(" ").slice(1).join(" ")}</div>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT CARD */}
        <div style={{
          background: G.white,
          borderRadius: 16,
          padding: "32px 28px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.08)",
          border: `1px solid ${G.border}`
        }}>
          <div style={{
            fontSize: 11,
            letterSpacing: 2,
            color: G.tLight,
            marginBottom: 10
          }}>
            SAMPLE 3KW SYSTEM · THRISSUR
          </div>

          <div style={{
            fontFamily: "'Cormorant Garamond',serif",
            fontSize: 48,
            fontWeight: 700,
            color: G.teal
          }}>
            ₹4,800
          </div>
          <div style={{ color: G.tLight, fontSize: 13, marginBottom: 20 }}>
            saved every 2-month bill
          </div>

          {[
            ["PM Surya Ghar Subsidy", "₹78,000"],
            ["Net system cost", "~₹72,000"],
            ["Units generated", "~720 units"],
            ["Payback", "~5–6 yrs"]
          ].map(([l, v], i) => (
            <div key={i} style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "10px 12px",
              borderRadius: 8,
              background: "#F5F7FA",
              marginBottom: 10,
              fontSize: 13
            }}>
              <span>{l}</span>
              <strong>{v}</strong>
            </div>
          ))}

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
            {["KSEB Approved", "DBT Subsidy", "25yr Warranty"].map(t => (
              <span key={t} style={{
                fontSize: 11,
                background: "rgba(78,205,196,0.12)",
                color: G.teal,
                padding: "6px 10px",
                borderRadius: 14
              }}>{t}</span>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

const EnergyNarrative: FC = () => {
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const stickyRef = useRef<HTMLDivElement>(null);
  const progress = useScrollProgress(stickyRef);

  const b1 = eoe(c01(progress, 0, .25));
  const b2 = eoe(c01(progress, .25, .5));
  const b3 = eoe(c01(progress, .5, .75));
  const b4 = eoe(c01(progress, .75, 1));
  const night = b4;

  return (
    <section id="howitworks" ref={stickyRef} style={{ height: isMobile ? "260vh" : "420vh", position: "relative" }}>
      <div style={{
        position: "sticky", top: 0, minHeight: "100vh", overflow: "hidden",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "96px 24px 56px",
      }}>
        <EnergyBg progress={progress} />

        <div style={{ position: "relative", zIndex: 1, width: "100%", maxWidth: 1080 }}>
          <div style={{ textAlign: "center", marginBottom: 34 }}>
            <Eyebrow center light={night > .45}>Path of Power</Eyebrow>
            <h2 style={{
              fontFamily: "'Cormorant Garamond',serif",
              fontSize: "clamp(2rem,5vw,3.4rem)", fontWeight: 700,
              color: night > .5 ? G.white : G.navy,
              lineHeight: 1.1, transition: "color .6s ease",
            }}>
              The HelioVAA Energy Ecosystem
            </h2>
            <p style={{
              color: night > .5 ? "rgba(255,255,255,.62)" : G.tMid,
              maxWidth: 620, margin: "16px auto 0", lineHeight: 1.8,
              transition: "color .6s ease",
            }}>
              Scroll through harvest, conversion, storage, and night-time autonomy.
            </p>
          </div>

          <div style={{
            borderRadius: 16,
            background: night > .35
              ? "linear-gradient(180deg, rgba(6,15,30,.85), rgba(6,15,30,.6))"
              : "linear-gradient(180deg, rgba(255,255,255,.7), rgba(247,243,235,.6))",
            backdropFilter: "blur(16px)",
            border: `1px solid rgba(201,168,76,${lerp(.12, .3, Math.max(b1, b2, b3, b4))})`,
            boxShadow: isMobile
              ? "0 8px 20px rgba(0,0,0,.08)"
              : `
      0 10px 30px rgba(0,0,0,${lerp(.05, .12, b2)}),
      0 30px 60px rgba(0,0,0,${lerp(.08, .2, b4)}),
      inset 0 1px 0 rgba(255,255,255,0.08)
    `,
            padding: "28px 18px 34px",
            transition: "background .6s ease,border-color .4s ease,box-shadow .4s ease",
            overflow: "hidden",
          }}>
            <svg viewBox="0 0 1000 280" preserveAspectRatio="xMidYMid meet" style={{
              width: "100%",
              overflow: "visible",
              transform: "translateZ(0)",
              willChange: "transform"
            }}>
              <path d={PA} fill="none" stroke={G.gold} strokeWidth="1.5" opacity={lerp(.12, .76, b1)} strokeDasharray="6 4" />
              <path d={PB1} fill="none" stroke={G.gold} strokeWidth="1.5" opacity={lerp(.12, .76, b2)} strokeDasharray="6 4" />
              <path d={PB2} fill="none" stroke={G.gold} strokeWidth="1" opacity={lerp(.1, .58, b3)} strokeDasharray="4 6" />
              <path d={PC} fill="none" stroke={G.gold} strokeWidth="1.5" opacity={lerp(.12, .72, b2)} strokeDasharray="6 4" />
              <path d={PREV} fill="none" stroke={G.teal} strokeWidth="1.8" opacity={lerp(0, .86, b4)} strokeDasharray="5 4" />

              {b1 > .1 && (isMobile ? [0] : [0, .45, .9, 1.35]).map((d, i) => <Pulse key={`h-${i}`} path={PA} delay={d} dur={2.2} />)}
              {b2 > .1 && (isMobile ? [0] : [0, .5, 1]).map((d, i) => <Pulse key={`c-${i}`} path={PB1} delay={d} dur={2.35} />)}
              {!isMobile && b2 > .2 && [.25, .85, 1.45].map((d, i) => <Pulse key={`s-${i}`} path={PB2} delay={d} dur={2.75} color={G.gold} />)}
              {b4 > .1 && (isMobile ? [0] : [0, .5, 1, 1.5]).map((d, i) => <Pulse key={`r-${i}`} path={PREV} delay={d} dur={2} color={G.teal} rev />)}

              {/* SOLAR PANEL */}
              <g
                style={{
                  opacity: b1,
                  transform: `translateY(${(1 - b1) * 40}px)`,
                  transition: "all .6s cubic-bezier(0.22,1,0.36,1)"
                }}
              >
                <g transform={`translate(112,110) scale(${1 + b1 * 0.03})`}>
                  <g transform="translate(-40,-30)">
                    {[0, 1, 2].map(r => [0, 1, 2].map(c => (
                      <rect key={`${r}${c}`} x={c * 22} y={r * 16} width="20" height="14" rx="2"
                        fill="none" stroke={G.gold} strokeWidth={lerp(0.8, 1.6, b1)} opacity={0.9} />
                    )))}
                  </g>
                  <text y="80" textAnchor="middle" fill={G.gold} fontSize="11" letterSpacing="2">SOLAR ARRAY</text>
                </g>
              </g>

              {/* INVERTER */}
              <g
                style={{
                  opacity: b2,
                  transform: `translateY(${(1 - b2) * 40}px)`,
                  transition: "all .6s cubic-bezier(0.22,1,0.36,1)"
                }}
              >
                <g transform={`translate(370,110) scale(${1 + b2 * 0.03})`}>
                  <g transform="translate(-28,-28)">
                    <rect x="0" y="0" width="56" height="56" rx="6" fill={G.navy} stroke={G.gold} strokeWidth={1.5} />
                    <line x1="12" y1="28" x2="44" y2="28" stroke={G.goldS} strokeWidth={2} opacity={b2} />
                    <line x1="20" y1="18" x2="36" y2="38" stroke={G.goldS} strokeWidth={1.5} opacity={b2} />
                  </g>
                  <text y="80" textAnchor="middle" fill={G.gold} fontSize="11" letterSpacing="2">INVERTER</text>
                </g>
              </g>

              {/* HOME */}
              <g
                style={{
                  opacity: b2,
                  transform: `translateY(${(1 - b2) * 40}px)`,
                  transition: "all .6s cubic-bezier(0.22,1,0.36,1)"
                }}
              >
                <g transform={`translate(890,110) scale(${1 + b2 * 0.03})`}>
                  <g transform="translate(-34,-34)">
                    <polygon points="34,6 6,28 62,28" fill="none" stroke={b2 > .5 ? G.teal : G.gold} strokeWidth="1.5" />
                    <rect x="10" y="28" width="48" height="36" rx="2" fill="none" stroke={b2 > .5 ? G.teal : G.gold} strokeWidth="1.5" />
                    <rect x="18" y="36" width="10" height="10" fill={b2 > .5 ? G.goldS : "rgba(255,255,255,.4)"} />
                    <rect x="40" y="36" width="10" height="10" fill={b2 > .5 ? G.goldS : "rgba(255,255,255,.4)"} />
                  </g>
                  <text y="80" textAnchor="middle" fill={b2 > .5 ? G.teal : G.gold} fontSize="11" letterSpacing="2">HOME</text>
                </g>
              </g>

              {/* BATTERY */}
              <g
                style={{
                  opacity: b2,
                  transform: `translateY(${(1 - b2) * 40}px)`,
                  transition: "all .6s cubic-bezier(0.22,1,0.36,1)"
                }}
              >
                <g transform={`translate(634,160) scale(${1 + b2 * 0.03})`}>
                  <g transform="translate(-20,-40)">
                    <rect x="0" y="0" width="40" height="80" rx="6" fill="none" stroke={G.gold} strokeWidth={1.5} />
                    <rect x="4" y={80 - 70 * b3} width="32" height={70 * b3} rx="3" fill={G.goldS} opacity="0.7" />
                  </g>
                  <text y="70" textAnchor="middle" fill={G.gold} fontSize="11" letterSpacing="2">BATTERY</text>
                </g>
              </g>

              {/* Removed narrative mode text from SVG */}
            </svg>
          </div>

          <div
            style={{
              marginTop: 28,
              textAlign: "center",
              color: night > .5 ? G.teal : G.goldD,
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: 3,
              fontFamily: "DM Sans",
              opacity: b1 > .2 ? 1 : 0,
              transform: `translateY(${b1 > .2 ? 0 : 10}px)`,
              transition: "all .6s cubic-bezier(0.22,1,0.36,1)"
            }}
          >
            {night > .5 ? "NIGHT MODE · BATTERY TO HOME" : "DAY MODE · SOLAR TO INVERTER TO HOME"}
          </div>

          <div style={{ marginTop: 24, display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            {[
              ["Harvest", b1],
              ["Conversion", b2],
              ["Storage", b3],
              ["Autonomy", b4],
            ].map(([label, p]) => (
              <div key={label as string} style={{ width: 96 }}>
                <div style={{ height: 3, borderRadius: 3, background: "rgba(201,168,76,.18)", overflow: "hidden" }}>
                  <div style={{ width: `${(p as number) * 100}%`, height: "100%", borderRadius: 3, background: `linear-gradient(90deg,${G.gold},${G.goldS})`, transition: "width .15s ease-out" }} />
                </div>
                <div style={{ marginTop: 7, textAlign: "center", color: (p as number) > .1 ? (night > .5 ? G.goldL : G.goldD) : G.tLight, fontSize: 9, letterSpacing: 1.4, textTransform: "uppercase", fontWeight: 600 }}>
                  {label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const HowItWorks: FC = () => <EnergyNarrative />;

// ─── SOLUTION SECTION ─────────────────────────────────────────────────────────
const Solution: FC = () => (
  <section id="solution" style={{ background: G.cream, padding: "100px 24px" }}>
    <div style={{ maxWidth: 1040, margin: "0 auto" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(340px,1fr))", gap: 64, alignItems: "center" }}>
        <div className="sr-l">
          <Eyebrow>The Problem</Eyebrow><GLine />
          <h2 style={{
            fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(1.8rem,4vw,2.6rem)",
            fontWeight: 700, color: G.navy, lineHeight: 1.3, marginBottom: 20
          }}>
            Rising Bills Are Quietly Draining Your Wealth
          </h2>
          <p style={{ color: G.tMid, fontSize: 16, lineHeight: 1.85, fontWeight: 300, marginBottom: 32 }}>
            Kerala's electricity tariffs have tripled over the past decade. At ₹5,000/month you're
            spending <strong style={{ color: G.navy }}>over ₹6 lakhs every 10 years</strong> — money that could build a real asset.
          </p>
          {[
            "KSEB tariff hikes every 1–2 years",
            "Kerala gets 5–6 peak sun hours daily",
            "Central subsidy up to ₹78,000 available",
            "KSERC 2025 rules: 3kW fully net-metered (no battery needed)"
          ].map(t => (
              <div key={t} style={{ display: "flex", gap: 14, marginBottom: 14, alignItems: "flex-start" }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: G.gold, marginTop: 8, flexShrink: 0 }} />
                <span style={{ color: G.navy, fontSize: 15, lineHeight: 1.6 }}>{t}</span>
              </div>
            ))}
          <p style={{ color: G.tMid, fontSize: 14, marginTop: 20 }}>
            Important: KSERC Renewable Energy Regulations 2025 (effective Nov 6, 2025) change
            net metering eligibility and system sizing. We ensure your system is fully compliant.
          </p>
        </div>
        <div className="stag" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          {[["3×", "Tariff increase since 2012"], ["₹6L+", "Wasted per decade at ₹5k/mo"],
          ["5–6h", "Peak sun hours in Kerala"], ["25yr", "Solar system lifespan"]].map(([n, l]) => (
            <div key={l} className="sr card" style={{
              background: G.white, border: `1px solid ${G.border}`,
              borderRadius: 8, padding: "28px 20px", boxShadow: "0 4px 20px rgba(0,0,0,.04)"
            }}>
              <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 38, fontWeight: 700, color: G.gold, lineHeight: 1 }}>{n}</div>
              <div style={{ color: G.tMid, fontSize: 13, marginTop: 8, lineHeight: 1.5 }}>{l}</div>
            </div>
          ))}
        </div>
      </div>
      {/* Comparison */}
      <div style={{ marginTop: 80, textAlign: "center" }}>
        <div className="sr"><Eyebrow center>The Choice</Eyebrow><GLine center />
          <h2 style={{
            fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(1.8rem,4vw,2.6rem)",
            fontWeight: 700, color: G.navy, marginBottom: 48
          }}>Your Bill vs HelioVAA EMI</h2>
        </div>
        <div className="stag" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 24 }}>
          <div className="sr card" style={{
            background: G.white, borderRadius: 8, padding: "40px 32px",
            border: "1px solid #E2E8F0", boxShadow: "0 4px 20px rgba(0,0,0,.05)"
          }}>
            <p style={{
              fontSize: 10, letterSpacing: 2, color: "#E53E3E", fontWeight: 600,
              textTransform: "uppercase" as const, marginBottom: 16
            }}>Without Solar</p>
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 52, fontWeight: 700, color: "#E53E3E", lineHeight: 1 }}>₹5,000</div>
            <div style={{ color: G.tLight, fontSize: 13, marginBottom: 32 }}>per month, forever increasing</div>
            {["Money gone permanently", "Annual tariff hikes", "Zero asset value", "Full grid dependency"].map(t => (
              <div key={t} style={{ display: "flex", gap: 10, marginBottom: 10, textAlign: "left" as const }}>
                <span style={{ color: "#E53E3E", fontSize: 16 }}>—</span>
                <span style={{ color: G.tMid, fontSize: 14 }}>{t}</span>
              </div>
            ))}
          </div>
          <div className="sr card" style={{
            background: G.navy, borderRadius: 8, padding: "40px 32px",
            boxShadow: "0 12px 40px rgba(13,31,53,.2)", border: `1px solid rgba(201,168,76,.3)`, position: "relative", overflow: "hidden"
          }}>
            <div style={{
              position: "absolute", top: -60, right: -60, width: 200, height: 200, borderRadius: "50%",
              background: "radial-gradient(circle,rgba(201,168,76,.12) 0%,transparent 70%)", pointerEvents: "none"
            }} />
            <p style={{
              fontSize: 10, letterSpacing: 2, color: G.gold, fontWeight: 600,
              textTransform: "uppercase" as const, marginBottom: 16
            }}>With HelioVAA</p>
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 52, fontWeight: 700, color: G.gold, lineHeight: 1 }}>₹2,100</div>
            <div style={{ color: "rgba(255,255,255,.5)", fontSize: 13, marginBottom: 32 }}>fixed monthly EMI</div>
            {["Build a ₹3–5L asset", "Fixed EMI, no surprises", "Earn via net metering", "Subsidy reduces your cost"].map(t => (
              <div key={t} style={{ display: "flex", gap: 10, marginBottom: 10, textAlign: "left" as const }}>
                <span style={{ color: G.gold, fontSize: 16 }}>+</span>
                <span style={{ color: "rgba(255,255,255,.8)", fontSize: 14 }}>{t}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ─── PROCESS ─────────────────────────────────────────────────────────────────
const Process: FC = () => (
  <section id="process" style={{ background: G.white, padding: "100px 24px" }}>
    <div style={{ maxWidth: 980, margin: "0 auto" }}>
      <div className="sr" style={{ textAlign: "center", marginBottom: 64 }}>
        <Eyebrow center>Our Process</Eyebrow><GLine center />
        <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(1.8rem,4vw,2.6rem)", fontWeight: 700, color: G.navy }}>
          From Consultation to Savings in 3 Steps
        </h2>
      </div>
      <div className="stag" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 28 }}>
        {[
          { n: "I", icon: "◎", title: "Site Consultation", desc: "Our expert visits, analyses your roof and consumption, and presents a detailed savings projection — completely free." },
          { n: "II", icon: "◈", title: "Design & Installation", desc: "Certified engineers install in 1–2 days. We handle KSEB approvals, net metering setup and all subsidy paperwork." },
          { n: "III", icon: "◉", title: "Monitor & Earn", desc: "Live app shows generation data. Surplus earns KSEB credits. Your bill drops from the very first billing cycle." },
        ].map(({ n, icon, title, desc }) => (
          <div key={n} className="sr card" style={{
            background: G.cream, borderRadius: 8, padding: "40px 32px",
            border: `1px solid ${G.border}`, boxShadow: "0 4px 20px rgba(0,0,0,.04)"
          }}>
            <div style={{
              fontSize: 10, letterSpacing: 3, color: G.gold, textTransform: "uppercase" as const,
              fontWeight: 600, marginBottom: 20, fontFamily: "DM Sans"
            }}>Step {n}</div>
            <div style={{ fontSize: 26, color: G.gold, marginBottom: 16 }}>{icon}</div>
            <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, fontWeight: 700, color: G.navy, marginBottom: 12 }}>{title}</h3>
            <p style={{ color: G.tMid, fontSize: 14, lineHeight: 1.8, fontWeight: 300 }}>{desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── CALCULATOR ───────────────────────────────────────────────────────────────
const Calculator: FC = () => {
  const [bill, setBill] = useState(5000);
  const [category, setCategory] = useState("Domestic (LT I)");

  // ─── REAL CALCULATION ENGINE ───

  let tariffMultiplier = 1;
  if (category === "Commercial (LT IV)") tariffMultiplier = 1.25;
  if (category === "Industrial / Other") tariffMultiplier = 1.4;

  const adjustedBill = bill * tariffMultiplier;

  // Estimate units
  const units = adjustedBill / 6;

  // System size (kW)
  const systemKW = Math.min(10, Math.max(1, units / 120));

  // System cost
  const costPerKW = 60000;
  const systemCost = systemKW * costPerKW;

  // Subsidy
  let subsidy = 0;
  if (systemKW <= 2) subsidy = systemKW * 30000;
  else subsidy = 78000;

  // Net cost
  const netCost = systemCost - subsidy;

  // Monthly savings
  const monthlySavings = adjustedBill * 0.8;

  // Payback
  const payback = (netCost / (monthlySavings * 12)).toFixed(1);

  return (
    <section id="calculator" style={{
      background: `linear-gradient(160deg,${G.navy} 0%,${G.navyM} 100%)`,
      padding: "100px 24px",
      position: "relative",
      overflow: "hidden"
    }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>

        <div className="sr" style={{ textAlign: "center", marginBottom: 52 }}>
          <Eyebrow center light>Savings Calculator</Eyebrow>
          <GLine center />
          <h2 style={{
            fontFamily: "'Cormorant Garamond',serif",
            fontSize: "clamp(1.8rem,4vw,2.6rem)",
            fontWeight: 700,
            color: G.white
          }}>
            How Much Can You Save?
          </h2>
        </div>

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))",
          gap: 32
        }}>

          {/* LEFT INPUT PANEL */}
          <div style={{
            background: "rgba(255,255,255,.05)",
            border: "1px solid rgba(201,168,76,.2)",
            borderRadius: 12,
            padding: "32px"
          }}>

            <div style={{ marginBottom: 20 }}>
              <div style={{ color: G.tLight, fontSize: 12, marginBottom: 8 }}>AVERAGE BILL</div>
              <input
                type="range"
                min={1000}
                max={15000}
                step={500}
                value={bill}
                onChange={e => setBill(Number(e.target.value))}
                style={{ width: "100%" }}
              />
              <div style={{ color: G.gold, fontSize: 20, marginTop: 8 }}>₹{bill}</div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ color: G.tLight, fontSize: 12, marginBottom: 6 }}>CATEGORY</div>
              <select
                value={category}
                onChange={e => setCategory(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: 6,
                  background: "rgba(255,255,255,.1)",
                  color: G.white,
                  border: "1px solid rgba(201,168,76,.3)"
                }}
              >
                <option>Domestic (LT I)</option>
                <option>Commercial (LT IV)</option>
                <option>Industrial / Other</option>
              </select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ color: G.tLight, fontSize: 12 }}>SYSTEM SIZE</div>
              <div style={{ color: G.white, fontSize: 16 }}>{systemKW.toFixed(1)} kW</div>
            </div>

            <button className="btn-g" style={{ width: "100%", marginTop: 16 }}>
              Calculate My Savings ⚡
            </button>
          </div>

          {/* RIGHT RESULT PANEL */}
          <div style={{
            background: "rgba(255,255,255,.08)",
            border: "1px solid rgba(201,168,76,.25)",
            borderRadius: 12,
            padding: "32px"
          }}>

            <div style={{
              fontFamily: "'Cormorant Garamond',serif",
              fontSize: 44,
              fontWeight: 700,
              color: G.gold
            }}>
              ₹{Math.round(monthlySavings * 2).toLocaleString()}
            </div>

            <div style={{ color: G.tLight, marginBottom: 20 }}>
              Estimated savings per 2 months
            </div>

            {[
              ["System Cost (before subsidy)", `₹${Math.round(systemCost).toLocaleString()}`],
              ["PM Surya Ghar Subsidy", `- ₹${Math.round(subsidy).toLocaleString()}`],
              ["Net Investment", `₹${Math.round(netCost).toLocaleString()}`],
              ["Units Generated / month", `${Math.round(systemKW * 120)} units`],
              ["Annual Savings", `₹${Math.round(monthlySavings * 12).toLocaleString()}`],
              ["Payback Period", `${payback} yrs`]
            ].map(([l, v], i) => (
              <div key={i} style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "10px 12px",
                borderRadius: 6,
                background: "rgba(255,255,255,.05)",
                marginBottom: 10,
                fontSize: 13
              }}>
                <span style={{ color: G.tLight }}>{l}</span>
                <strong style={{ color: G.white }}>{v}</strong>
              </div>
            ))}

            <div style={{ color: "#22c55e", fontWeight: 600, fontSize: 13, marginTop: 10 }}>
              ⚡ {Math.round((monthlySavings / bill) * 100)}% bill reduction
            </div>

            <div style={{
              marginTop: 16,
              height: 6,
              borderRadius: 4,
              background: "rgba(255,255,255,.1)",
              overflow: "hidden"
            }}>
              <div style={{
                width: "72%",
                height: "100%",
                background: `linear-gradient(90deg,${G.gold},${G.goldS})`
              }} />
            </div>

            <div style={{
              fontSize: 12,
              color: G.gold,
              marginTop: 6,
              textAlign: "center"
            }}>
              ~9x return over 25 years
            </div>

          </div>

        </div>
      </div>
    </section>
  );
};

// ─── TESTIMONIALS ────────────────────────────────────────────────────────────
const Testimonials: FC = () => (
  <section id="testimonials" style={{ background: G.creamL, padding: "100px 24px" }}>
    <div style={{ maxWidth: 1000, margin: "0 auto", textAlign: "center" }}>
      <div className="sr"><Eyebrow center>Client Stories</Eyebrow><GLine center />
        <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(1.8rem,4vw,2.6rem)", fontWeight: 700, color: G.navy, marginBottom: 52 }}>
          What Our Clients Say
        </h2>
      </div>
      <div className="stag" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 24 }}>
        {[
          { name: "Suresh Kumar", loc: "Thrissur", bill: "₹6,200 → ₹1,800/mo", text: "Professional team, completed in one day, bill dropped immediately. HelioVAA exceeded every expectation." },
          { name: "Meena Nair", loc: "Ernakulam", bill: "₹4,800 → ₹1,400/mo", text: "The transparency throughout was refreshing. Every step clearly explained, subsidy paperwork handled seamlessly." },
          { name: "Rajesh Pillai", loc: "Kozhikode", bill: "₹7,400 → ₹2,100/mo", text: "ROI far better than any fixed deposit. I built a real asset on my rooftop. Should have done this years ago." },
        ].map(({ name, loc, bill, text }) => (
          <div key={name} className="sr card" style={{
            background: G.white, borderRadius: 8, padding: "36px 28px",
            border: `1px solid ${G.border}`, boxShadow: "0 4px 16px rgba(0,0,0,.04)", textAlign: "left" as const
          }}>
            <div style={{ marginBottom: 16 }}>{"★★★★★".split("").map((s, i) => <span key={i} style={{ color: G.gold, fontSize: 16 }}>{s}</span>)}</div>
            <p style={{ color: G.tMid, fontSize: 14.5, lineHeight: 1.85, marginBottom: 24, fontWeight: 300, fontStyle: "italic" }}>"{text}"</p>
            <div style={{ paddingTop: 20, borderTop: `1px solid ${G.border}`, display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{
                width: 42, height: 42, borderRadius: "50%", background: `linear-gradient(135deg,${G.gold},${G.goldL})`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontFamily: "'Cormorant Garamond',serif", fontWeight: 700, color: G.navy, fontSize: 18
              }}>{name[0]}</div>
              <div>
                <div style={{ fontWeight: 600, color: G.navy, fontSize: 14 }}>{name}</div>
                <div style={{ color: G.tLight, fontSize: 12, marginTop: 2 }}>{loc} · {bill}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── FAQ SECTION ─────────────────────────────────────────────────────────────
const FAQ: FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      q: "Is my home eligible for net metering?",
      a: "Yes. Under KSERC Renewable Energy Regulations 2025, most Kerala homes qualify. 3kW systems are fully net-metered with no storage requirement.",
      wa: "https://wa.me/917306061397?text=I want to check net metering eligibility"
    },
    {
      q: "What system size is best for my home?",
      a: "For most homes, 3kW is the sweet spot—maximum subsidy, full net metering, and optimal ROI.",
      wa: "https://wa.me/917306061397?text=Help me choose system size"
    },
    {
      q: "How long does installation take?",
      a: "Typically 30–45 days including KSEB approvals and net meter installation.",
      wa: "https://wa.me/917306061397?text=I want installation timeline details"
    },
    {
      q: "Will solar work during power cuts?",
      a: "Standard systems shut off for safety. Battery-backed systems can provide backup if needed.",
      wa: "https://wa.me/917306061397?text=Tell me about solar backup options"
    }
  ];

  return (
    <section id="faq" style={{ background: G.white, padding: "100px 24px" }}>
      <div style={{ maxWidth: 800, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <Eyebrow center>FAQ</Eyebrow>
          <GLine center />
          <h2 style={{
            fontFamily: "'Cormorant Garamond',serif",
            fontSize: "clamp(1.8rem,4vw,2.6rem)",
            fontWeight: 700,
            color: G.navy
          }}>
            Your Questions, Answered
          </h2>
        </div>

        {faqs.map((f, i) => {
          const isOpen = openIndex === i;

          return (
            <div key={i} style={{
              border: `1px solid ${G.border}`,
              borderRadius: 8,
              marginBottom: 16,
              background: G.cream,
              overflow: "hidden",
              transition: "all .3s ease"
            }}>
              {/* Question Header */}
              <div
                onClick={() => setOpenIndex(isOpen ? null : i)}
                style={{
                  padding: "18px 20px",
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  fontWeight: 600,
                  color: G.navy
                }}
              >
                <span>{f.q}</span>
                <span style={{
                  transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform .3s"
                }}>
                  ▼
                </span>
              </div>

              {/* Answer + WhatsApp */}
              {isOpen && (
                <div style={{ padding: "0 20px 20px" }}>
                  <div style={{
                    color: G.tMid,
                    fontSize: 14,
                    marginBottom: 14,
                    lineHeight: 1.6
                  }}>
                    {f.a}
                  </div>

                  <a
                    href={f.wa}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      background: "#25D366",
                      color: "#fff",
                      padding: "10px 16px",
                      borderRadius: 6,
                      textDecoration: "none",
                      fontSize: 13,
                      fontWeight: 600
                    }}
                  >
                    <WaIcon />
                    Ask on WhatsApp
                  </a>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

// ─── CONTACT ─────────────────────────────────────────────────────────────────
const Contact: FC = () => {
  const [form, setForm] = useState({ name: "", phone: "", city: "", bill: "" });
  const [done, setDone] = useState(false);
  const [focused, setFocused] = useState("");
  const iStyle = (k: string): CSSProperties => ({
    width: "100%", padding: "13px 16px",
    border: `1px solid ${focused === k ? G.gold : G.border}`, borderRadius: 4,
    fontSize: 15, color: G.navy, background: focused === k ? G.white : G.cream,
    transition: "all .3s cubic-bezier(0.22,1,0.36,1)", fontFamily: "'DM Sans',sans-serif",
    boxShadow: focused === k ? `0 0 0 3px rgba(201,168,76,.15)` : "none",
  });
  return (
    <section id="contact" style={{ background: G.white, padding: "100px 24px" }}>
      <div style={{ maxWidth: 580, margin: "0 auto" }}>
        <div className="sr" style={{ textAlign: "center", marginBottom: 48 }}>
          <Eyebrow center>Free Consultation</Eyebrow><GLine center />
          <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(1.8rem,4vw,2.6rem)", fontWeight: 700, color: G.navy, marginBottom: 12 }}>
            Request Your Free Solar Estimate
          </h2>
          <p style={{ color: G.tMid, fontSize: 15, fontWeight: 300 }}>No obligation. Our expert contacts you within 24 hours.</p>
        </div>
        <div className="sr">
          {done ? (
            <div style={{ background: G.cream, borderRadius: 8, padding: "56px 40px", textAlign: "center", border: `1px solid ${G.border}` }}>
              <div style={{
                width: 68, height: 68, borderRadius: "50%", background: `linear-gradient(135deg,${G.gold},${G.goldL})`,
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28,
                color: G.navy, margin: "0 auto 24px", fontWeight: 700
              }}>✓</div>
              <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 26, fontWeight: 700, color: G.navy, marginBottom: 12 }}>Thank You</h3>
              <p style={{ color: G.tMid, fontSize: 15, lineHeight: 1.8, marginBottom: 32, fontWeight: 300 }}>
                A HelioVAA consultant will reach out within 24 hours with your personalised savings report.
              </p>
              <WaBtn />
            </div>
          ) : (
            <div style={{ background: G.cream, borderRadius: 8, padding: "48px 40px", border: `1px solid ${G.border}` }}>
              {[["name", "Full Name", "Your name", "text"], ["phone", "Mobile Number", "10-digit number", "tel"],
              ["city", "City / Town", "e.g. Thrissur, Kochi", "text"], ["bill", "Monthly EB Bill (₹)", "e.g. 5000", "number"]].map(([k, l, ph, t]) => (
                <div key={k} style={{ marginBottom: 22 }}>
                  <label style={{
                    display: "block", fontSize: 10, fontWeight: 600, letterSpacing: 1.5,
                    textTransform: "uppercase" as const, color: G.navy, marginBottom: 8
                  }}>{l}</label>
                  <input type={t} placeholder={ph} value={form[k as keyof typeof form]}
                    onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))}
                    onFocus={() => setFocused(k)} onBlur={() => setFocused("")}
                    style={iStyle(k)} />
                </div>
              ))}
              <button className="btn-g" onClick={() => setDone(true)}
                style={{ width: "100%", padding: "17px", borderRadius: 4, fontSize: 12, letterSpacing: 2 }}>
                Request Free Estimate →
              </button>
              <p style={{ color: G.tLight, fontSize: 12, textAlign: "center" as const, marginTop: 14 }}>
                Your information is private and never shared.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// ─── FOOTER ──────────────────────────────────────────────────────────────────
const Footer: FC<{ scrollTo: (id: string) => void }> = ({ scrollTo }) => (
  <footer style={{ background: `linear-gradient(160deg,${G.navy} 0%,#0A192E 100%)`, padding: "96px 24px", textAlign: "center" }}>
    <div className="sr" style={{ maxWidth: 620, margin: "0 auto" }}>
      <Logo size={38} light />
      <div style={{ margin: "32px auto", width: 56, height: 1, background: `linear-gradient(90deg,transparent,${G.gold},transparent)` }} />
      <h2 style={{
        fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(2rem,5vw,3rem)",
        fontWeight: 700, color: G.white, margin: "32px 0 16px", lineHeight: 1.2
      }}>
        Stop Paying the Grid.<br /><span style={{ color: G.gold }}>Start Owning Your Energy.</span>
      </h2>
      <p style={{ color: "rgba(255,255,255,.5)", fontSize: 16, fontWeight: 300, lineHeight: 1.8, marginBottom: 40 }}>
        Every month you wait costs ₹2,000–₹4,500. Join 500+ Kerala homeowners who chose HelioVAA.
      </p>
      <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
        <button className="btn-g" onClick={() => scrollTo("contact")}>Get Free Estimate</button>
        <WaBtn />
      </div>
      <p style={{ color: "rgba(255,255,255,.18)", fontSize: 10, marginTop: 52, letterSpacing: 1.5, textTransform: "uppercase" as const }}>
        © 2024 HelioVAA · MNRE Approved · Kerala
      </p>
    </div>
  </footer>
);

// ─── ROOT ─────────────────────────────────────────────────────────────────────
export default function App() {
  useEffect(() => inject(), []);
  useScrollReveal();
  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) { const top = el.getBoundingClientRect().top + window.scrollY - 64; window.scrollTo({ top, behavior: "smooth" }); }
  };
  return (
    <div style={{ fontFamily: "'DM Sans',sans-serif", background: G.creamL }}>
      <Nav scrollTo={scrollTo} />
      <Hero scrollTo={scrollTo} />
      <HowItWorks />
      <Solution />
      <Process />
      <Calculator />
      <Testimonials />
      <FAQ />
      <Contact />
      <Footer scrollTo={scrollTo} />
      <WaBtn floating />
    </div>
  );
}
